import { useEffect, useId, useState } from 'react';
import { MAX_CAPTION, MAX_LOCATION, type PhotoText } from '@mantelpiece/shared';

/**
 * The caption-and-location editor, shared by the frame's contact sheet and the
 * album page.
 *
 * One component for both because the fields, the limits and the "empty means
 * cleared" rule are the same in each — what differs is only where the save goes,
 * and that is the caller's business. On a frame it changes one placement; in an
 * album it changes every frame the album reaches. The wording says which.
 */

interface Props {
  readonly value: PhotoText;
  /** Both fields, every time. An edit says what the photo reads now. */
  readonly onSave: (text: PhotoText) => Promise<void>;
  readonly onCancel: () => void;
  /** Said under the fields, so nobody has to guess how far a save travels. */
  readonly reach: string;
}

export const PhotoTextForm = ({ value, onSave, onCancel, reach }: Props) => {
  const [caption, setCaption] = useState(value.caption ?? '');
  const [location, setLocation] = useState(value.location ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const captionId = useId();
  const locationId = useId();

  // Reopening on a different photo has to start from that photo's text rather
  // than whatever was last typed into the box.
  useEffect(() => {
    setCaption(value.caption ?? '');
    setLocation(value.location ?? '');
  }, [value.caption, value.location]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSave({
        caption: caption.trim() || null,
        location: location.trim() || null,
      });
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'That did not save.');
      setSaving(false);
    }
  };

  return (
    <form className="stack" onSubmit={(event) => void submit(event)}>
      <label className="field" htmlFor={captionId}>
        <span>Caption</span>
        <input
          id={captionId}
          className="input"
          value={caption}
          maxLength={MAX_CAPTION}
          placeholder="Beach, August"
          onChange={(event) => setCaption(event.target.value)}
        />
      </label>

      <label className="field" htmlFor={locationId}>
        <span>Where it was taken</span>
        <input
          id={locationId}
          className="input"
          value={location}
          maxLength={MAX_LOCATION}
          placeholder="Brighton beach"
          onChange={(event) => setLocation(event.target.value)}
        />
      </label>

      <p className="note">{reach} Clearing a box removes it.</p>
      {error && (
        <p className="alert" data-tone="bad">
          {error}
        </p>
      )}

      <span className="row-tight">
        <button type="submit" className="button button-small" disabled={saving}>
          {saving ? 'Saving' : 'Save'}
        </button>
        <button
          type="button"
          className="button button-quiet button-small"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </span>
    </form>
  );
};
